import React, { useState } from 'react';
import { useHistory } from 'react-router';

import {
  Button,
  Grid,
  IconButton,
  Paper,
  TextField,
  Tooltip,
  Typography,
} from '@material-ui/core';
import { Add, ArrowBack, Delete } from '@material-ui/icons';
import ReactSelect from 'react-select';

import { useKeycloak } from '@react-keycloak/web';

import { useStyles } from './styles';

import { useAxios } from '../../../utils/hooks';
import Loader from '../../../components/Loader';
import FileUploader from '../../../components/FileUploader';
import Snackbar, { AlertComponentProps } from '../../../components/Snackbar';
import {
  TicketCategory,
  TicketCategoryLabels,
  TicketPriority,
  TicketPriorityLabels,
  TicketRequester,
} from '../../../models/ticket';

const priorityOptions = Object.values(TicketPriority).map((value) => ({
  value,
  label: TicketPriorityLabels[value],
}));

const categoryOptions = Object.values(TicketCategory).map((value) => ({
  value,
  label: TicketCategoryLabels[value],
}));

const HelpdeskNew: React.FC = () => {
  const api = useAxios();
  const classes = useStyles();
  const history = useHistory();
  const { keycloak } = useKeycloak();

  // @ts-ignore
  const currentName = keycloak?.idTokenParsed?.name || '';
  // @ts-ignore
  const currentEmail = keycloak?.idTokenParsed?.email || '';

  const [loading, setLoading] = useState(false);
  const [title, setTitle] = useState('');
  const [description, setDescription] = useState('');
  const [priority, setPriority] = useState<TicketPriority>(
    TicketPriority.MEDIUM,
  );
  const [category, setCategory] = useState<TicketCategory>(
    TicketCategory.OTHER,
  );
  const [recordNumberInput, setRecordNumberInput] = useState('');
  const [recordNumbers, setRecordNumbers] = useState<number[]>([]);
  const [requesters, setRequesters] = useState<TicketRequester[]>([
    { name: currentName, email: currentEmail },
  ]);
  const [fileIds, setFileIds] = useState<string[]>([]);

  const [alert, setAlert] = useState<
    AlertComponentProps & { isActive: boolean }
  >({
    isActive: false,
    type: 'info',
    message: 'none',
  });

  const resetAlert = () => {
    setAlert({ isActive: false, type: 'info', message: 'none' });
  };

  const handleFileId = (fileId: string) => {
    if (fileId) {
      setFileIds((prev) => [...prev, fileId]);
    }
  };

  const handleAddRecordNumber = () => {
    const parsed = Number(recordNumberInput);

    if (
      recordNumberInput &&
      !Number.isNaN(parsed) &&
      !recordNumbers.includes(parsed)
    ) {
      setRecordNumbers((prev) => [...prev, parsed]);
    }

    setRecordNumberInput('');
  };

  const handleRemoveRecordNumber = (value: number) => {
    setRecordNumbers((prev) => prev.filter((number) => number !== value));
  };

  const handleAddRequester = () => {
    setRequesters((prev) => [...prev, { name: '', email: '' }]);
  };

  const handleRemoveRequester = (index: number) => {
    setRequesters((prev) => prev.filter((_, i) => i !== index));
  };

  const handleRequesterChange =
    (index: number, field: keyof TicketRequester) =>
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { value } = event.target;

      setRequesters((prev) =>
        prev.map((requester, i) =>
          i === index ? { ...requester, [field]: value } : requester,
        ),
      );
    };

  const handleSubmit = async () => {
    resetAlert();

    if (!title.trim() || !description.trim()) {
      setAlert({
        isActive: true,
        type: 'warning',
        message: 'Preencha título e descrição.',
      });
      return;
    }

    const validRequesters = requesters.filter(
      (requester) => requester.name.trim() && requester.email.trim(),
    );

    if (!validRequesters.length) {
      setAlert({
        isActive: true,
        type: 'warning',
        message: 'Informe ao menos um solicitante com nome e e-mail.',
      });
      return;
    }

    setLoading(true);

    try {
      await api.current?.post('/helpdesk', {
        title,
        description,
        recordNumbers,
        priority,
        category,
        requesters: validRequesters,
        files: fileIds,
      });

      history.push('/helpdesk', {
        alert: {
          type: 'success',
          message: 'Ticket criado com sucesso.',
        },
      });
    } catch (error) {
      setAlert({
        isActive: true,
        type: 'error',
        message: 'Erro ao criar ticket.',
      });
      setLoading(false);
    }
  };

  return (
    <>
      <Loader loading={loading} />

      <div className={classes.header}>
        <Button
          startIcon={<ArrowBack />}
          onClick={() => history.push('/helpdesk')}
        >
          Voltar
        </Button>

        <Typography variant="h6" className={classes.formTitles}>
          Novo Ticket
        </Typography>
      </div>

      <Paper className={classes.paper}>
        <Grid container spacing={2}>
          <Grid item xs={12}>
            <TextField
              label="Título"
              required
              fullWidth
              value={title}
              onChange={(event) => setTitle(event.target.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <TextField
              label="Descrição"
              required
              fullWidth
              multiline
              rows={4}
              value={description}
              onChange={(event) => setDescription(event.target.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" gutterBottom>
              Prioridade
            </Typography>
            <ReactSelect
              options={priorityOptions}
              value={priorityOptions.find(
                (option) => option.value === priority,
              )}
              onChange={(data: any) => setPriority(data.value)}
            />
          </Grid>

          <Grid item xs={12} sm={6}>
            <Typography variant="subtitle2" gutterBottom>
              Categoria
            </Typography>
            <ReactSelect
              options={categoryOptions}
              value={categoryOptions.find(
                (option) => option.value === category,
              )}
              onChange={(data: any) => setCategory(data.value)}
            />
          </Grid>

          <Grid item xs={12}>
            <Typography variant="subtitle2" gutterBottom>
              Prontuários referentes
            </Typography>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={8} sm={4}>
                <TextField
                  label="Nº Prontuário"
                  type="number"
                  fullWidth
                  value={recordNumberInput}
                  onChange={(event) => setRecordNumberInput(event.target.value)}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      event.preventDefault();
                      handleAddRecordNumber();
                    }
                  }}
                />
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  startIcon={<Add />}
                  onClick={handleAddRecordNumber}
                >
                  Adicionar
                </Button>
              </Grid>
            </Grid>

            <Grid container spacing={1} style={{ marginTop: '1%' }}>
              {recordNumbers.map((number) => (
                <Grid item key={number}>
                  <Button
                    variant="contained"
                    color="primary"
                    endIcon={<Delete />}
                    onClick={() => handleRemoveRecordNumber(number)}
                  >
                    {number}
                  </Button>
                </Grid>
              ))}
            </Grid>
          </Grid>

          <Grid item xs={12}>
            <div className={classes.header}>
              <Typography variant="subtitle2">Solicitantes</Typography>
              <Tooltip title="Adicionar solicitante">
                <IconButton color="primary" onClick={handleAddRequester}>
                  <Add />
                </IconButton>
              </Tooltip>
            </div>

            {requesters.map((requester, index) => (
              // eslint-disable-next-line react/no-array-index-key
              <Grid container spacing={1} alignItems="center" key={index}>
                <Grid item xs={12} sm={5}>
                  <TextField
                    label="Nome"
                    fullWidth
                    value={requester.name}
                    onChange={handleRequesterChange(index, 'name')}
                  />
                </Grid>
                <Grid item xs={12} sm={5}>
                  <TextField
                    label="E-mail"
                    type="email"
                    fullWidth
                    value={requester.email}
                    onChange={handleRequesterChange(index, 'email')}
                  />
                </Grid>
                <Grid item xs={12} sm={2}>
                  {requesters.length > 1 && (
                    <Tooltip title="Remover">
                      <IconButton
                        color="secondary"
                        onClick={() => handleRemoveRequester(index)}
                      >
                        <Delete />
                      </IconButton>
                    </Tooltip>
                  )}
                </Grid>
              </Grid>
            ))}
          </Grid>

          <Grid item xs={12}>
            <FileUploader handleFileId={handleFileId} />
          </Grid>

          <Grid item xs={12}>
            <Button
              variant="contained"
              color="primary"
              fullWidth
              onClick={handleSubmit}
            >
              Criar Ticket
            </Button>
          </Grid>
        </Grid>
      </Paper>

      {alert.isActive && <Snackbar {...alert} />}
    </>
  );
};

export default HelpdeskNew;
